import { Icon, BlankImg } from 'assets/images';
import React, { useState, useEffect } from 'react';
import { useEffectOnce } from 'hooks/useEffectOnce';
import {
  calculateVideoDuration,
  convertNoShowQualificationTime,
  convertUTCToLocalAmPmFormat,
} from 'utils/dateFormatter';
import { Rating } from 'react-simple-star-rating';
import PropTypes from 'prop-types';
import CopyIcon from 'components/CopyIcon/CopyIcon';
import UpgradePrompt from 'components/FeatureGate/UpgradePrompt';
import moment from 'moment';
import { DATE_FORMATS } from 'utils/constants';
import ImgComponent from 'components/imgComponent';
import { Collapse } from 'antd';
import Transcript from 'components/Transcript/Transcript';
import { getChatMessages, getDemoProsCons} from 'Services/demoService';
import logger from 'utils/logger';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {guestNoShow, success, backupSuccess, OffTopicIcon, TechDiffIcon } from 'assets/images';


/**
 * This component renders all the demo information for a single demo in the custom modal
 *
 * @return {ReactNode} A React element that renders all the demo information for a single demo in the custom modal
 */

const StorePayDetail = ({
  earning,
  guest,
  host,
  product,
  recording,
  startedAt,
  endedAt,
  soonestAt,
  stars,
  review,
  schedule,
  coupon_code,
  sentimentScore,
  summary,
  summaryLocked,
  sentimentLocked,
  demo_analysis,
  meetingParticipants,
  hasDiscussedBusinessStatus,
  faceTimeDuration,
  demoId,
  technicalUsers
}) => {
  const [hostProfile, setHostProfile] = useState();
  const [videoLink, setVideoLink] = useState();
  const [videoLoading, setVideoLoading] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [showExtraGuests, setShowExtraGuests] = useState(false);
  const hasTechIssue = (userType, userId) => {
    if (!Array.isArray(technicalUsers)) return false;

    return technicalUsers.some(
      u => u.user_type === userType && u.user_id === userId
    );
  };

  const guestReviews = (guest?.extras || [])
    .map((eg) => ({
      name: eg?.guest_detail?.full_name || "-",
      review: eg?.guest_detail?.guest_review_note || null,
    }))
    .filter((x) => x.review);


  const [prosConsData, setProsConsData] = useState({ pros: [], cons: [], locked: false });
  const [prosConsLoading, setProsConsLoading] = useState(false);
  /**
   * This component renders the scheduled time of a meeting
   *
   * @return {ReactNode} A React element that renders the scheduled time of a meeting
   */

  const getScheduledTime = () => {
    if (soonestAt) {
      return (
        <>
          {
            convertUTCToLocalAmPmFormat(
              `${schedule.scheduled_date}T${schedule.scheduled_time}`
            )[0]
          }
          {'  '}
          ASAP
        </>
      );
    }
    return (
      <>
        {
          convertUTCToLocalAmPmFormat(
            `${schedule.scheduled_date}T${schedule.scheduled_time}`
          )[0]
        }
        {'  '}
        {
          convertUTCToLocalAmPmFormat(
            `${schedule.scheduled_date}T${schedule.scheduled_time}`
          )[1]
        }
      </>
    );
  };

  /**
   * This component renders the renderNoShowQualification time of a meeting
   *
   * @return {ReactNode} A React element that renders the renderNoShowQualification time of a meeting
   */

  const renderNoShowQualification = () => {
    return (
      <>
        {convertNoShowQualificationTime(
          soonestAt,
          startedAt,
          `${schedule.scheduled_date}T${schedule.scheduled_time}`
        )}
      </>
    );
  };

  /**
   * This function is used to get the color of the host joining stat text
   *
   * @return {string} A string for the color of the host joining stat text
   */

  const getHostJoiningStatText = () => {
    if (soonestAt) {
      return '#212529';
    }

    if (!startedAt) {
      startedAt = `${schedule.scheduled_date}T${schedule.scheduled_time}`;
    }

    const hostStartTime = moment.utc(
      startedAt,
      DATE_FORMATS.GENERIC_FULL_DATE_FORMAT
    );

    const scheduledTime = moment.utc(
      `${schedule.scheduled_date}T${schedule.scheduled_time}`,
      DATE_FORMATS.GENERIC_FULL_DATE_FORMAT
    );

    hostStartTime.local();
    scheduledTime.local();

    if (scheduledTime < hostStartTime) return 'red';
    return '#212529';
  };

  /**
   * This function is used to get the color of the host joining stat text
   *
   * @return {string} A string for the color of the host joining stat text
   */

  const getHostLeftStatTextColor = () => {
    if (guest?.primary?.attend_meeting) {
      return '#212529';
    }
    const noShowTime = convertNoShowQualificationTime(
      soonestAt,
      startedAt,
      `${schedule.scheduled_date}T${schedule.scheduled_time}`,
      true
    );

    const hostLeftTime = moment.utc(
      endedAt,
      DATE_FORMATS.GENERIC_FULL_DATE_FORMAT
    );

    hostLeftTime.local();

    if (hostLeftTime < noShowTime) return 'red';
    return '#212529';
  };

  /**
   * This component renders the video duration time of a meeting
   *
   * @return {ReactNode} A React element that renders the video duration time of a meeting
   */

  const renderVideoDuration = () => {
    return (
      <>
        {calculateVideoDuration(
          startedAt,
          endedAt,
          guest?.primary?.attend_meeting,
          guest?.primary?.left_meeting,
          `${schedule.scheduled_date}T${schedule.scheduled_time}`
        )}
      </>
    );
  };

  /**
   * This component renders the facetime duration time of a meeting
   *
   * @return {ReactNode} A React element that renders the facetime duration time of a meeting
   */

  const renderFacetimeDuration = () => {
    return (
      <>
        {faceTimeDuration}
      </>
    );
  };

  // Get earliest guest joined time (primary + extras)
  const getFirstGuestJoinedTime = () => {
    const allJoinTimes = [
      guest?.primary?.attend_meeting,
      ...(guest?.extras || []).map((eg) => eg?.guest_detail?.attend_meeting),
    ].filter(Boolean);

    if (allJoinTimes.length === 0) return null;

    // earliest join
    return allJoinTimes.reduce((earliest, current) =>
      moment.utc(current).isBefore(moment.utc(earliest)) ? current : earliest
    );
  };

  // Get latest guest left time (primary + extras)
  const getLastGuestLeftTime = () => {
    const allLeftTimes = [
      guest?.primary?.left_meeting,
      ...(guest?.extras || []).map((eg) => eg?.guest_detail?.left_meeting),
    ].filter(Boolean);

    if (allLeftTimes.length === 0) return null;

    // latest left
    return allLeftTimes.reduce((latest, current) =>
      moment.utc(current).isAfter(moment.utc(latest)) ? current : latest
    );
  };


  /**
   * This component renders the statistics of an ended meeting
   *
   * @return {ReactNode} A React element that renders the statistics of an ended meeting for the store admin
   */

  const renderMeetingStats = () => {
    return (
      <>
        <div className="date-time-display">
          <div className="time-header-display">Scheduled</div>
          <div className="time-display">{getScheduledTime()}</div>
        </div>
        <div className="host-joined-display">
          <div className="host-joined-header-display">Real Customer Joined</div>
          <div
            className="host-joined-display"
            style={{ color: getHostJoiningStatText() }}
          >
            {
              convertUTCToLocalAmPmFormat(
                `${startedAt ? startedAt : schedule.scheduled_date}T${
                  schedule.scheduled_time
                }`
              )[1]
            }
          </div>
        </div>
        <div className="no-show-display">
          <div className="no-show-header-display">No Show Qualification</div>
          <div className="no-show-display">{renderNoShowQualification()}</div>
        </div>
        <div className="guest-joined-display">
          <div className="guest-joined-header-display">Guest Joined</div>
          <div className="guest-joined-display">
            {getFirstGuestJoinedTime() ? (
              <> {convertUTCToLocalAmPmFormat(getFirstGuestJoinedTime())[1]}</>
            ) : (
              <>NA</>
            )}
          </div>
        </div>
        <div className="guest-left-display">
          <div className="guest-left-header-display">Guest Left</div>
          <div className="guest-left-display">
            {getLastGuestLeftTime() ? (
              <> {convertUTCToLocalAmPmFormat(getLastGuestLeftTime())[1]}</>
            ) : (
              <>NA</>
            )}
          </div>
        </div>
        <div className="host-left-display">
          <div className="host-left-header-display">Real Customer Left</div>
          <div className="host-left-display">
            {endedAt ? (
              <div style={{ color: getHostLeftStatTextColor() }}>
                {' '}
                {convertUTCToLocalAmPmFormat(endedAt)[1]}
              </div>
            ) : (
              <>-</>
            )}
          </div>
        </div>
        <div className="face-time-duration-display">
          <div className="face-time-duration-header-display">
            Facetime Duration
          </div>
          <div className="face-time-duration-display">
            {renderFacetimeDuration()}
          </div>
        </div>
        <div className="video-duration-display">
          <div className="video-duration-header-display">Video Duration</div>
          <div className="video-duration-display">{renderVideoDuration()}</div>
        </div>
      </>
    );
  };

  /**
   * This component renders the screen recording of a demo.
   *
   * @return {ReactNode} A React element that renders the screen recording of a demo.
   */

  const getVideoUI = () => {
    if (videoLoading) {
      return (
        <div>
          <video
            key={videoLink}
            controls
            preload="auto"
            width="100%"
            style={{ borderRadius: '10px' }}
            playsInline
            loop
          >
            <source src={videoLink} type="video/mp4" />
          </video>
          {renderMeetingStats()}
        </div>
      );
    } else {
      if (videoLink) {
        return (
          <div>
            <video
              key={videoLink}
              controls
              width="100%"
              style={{ borderRadius: '10px' }}
              playsInline
              loop
            >
              <source src={videoLink} type="video/mp4" />
            </video>
            {renderMeetingStats()}
          </div>
        );
      } else {
        return 'Recording is corrupted, please contact the admin to get the recording';
      }
    }
  };

  /**
   * This fuction is used to get the video url of the screen recording
   *
   * @return {void}
   */

  const videoUrl = () => {
    const videoResponse = fetch(recording?.cloudinary_file);
    videoResponse
      .then(function (response) {
        if (response && response.status === 200) {
          setVideoLink(response.url);
        } else {
          setVideoLink(recording?.recorded_video);
        }
      })
      .catch(function (error) {
        console.log(error);
      })
      .finally(() => {
        setVideoLoading(false);
      });
  };

  /**
   * This fuction is used to get the profile picture of the host
   *
   * @return {void}
   */

  const profilePicture = () => {
    const response = fetch(host?.profile_picture?.cloudinary_file);
    response
      .then(function (response) {
        if (response && response.status === 200) {
          setHostProfile(response?.url);
        } else {
          setHostProfile(host?.profile_picture?.file);
        }
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  /**
   * Formats message time to local hh:mm AM/PM format
   *
   * @param {string|number} messageTime - The message time (timestamp or datetime string)
   * @return {string} Formatted time in hh:mm AM/PM format
   */
  const formatMessageTime = messageTime => {
    if (!messageTime) {
      return '';
    } else {
      try {
        let m;

        // If it's a timestamp (number in milliseconds), convert to moment
        if (typeof messageTime === 'number') {
          m = moment(messageTime);
        } else {
          // If it's a datetime string, try parsing as UTC first
          m = moment.utc(messageTime);
        }

        // Convert to local time and format
        m.local();
        return m.format(DATE_FORMATS.GENERIC_UTC_TIME_FORMAT);
      } catch (error) {
        logger.error('Error formatting message time:', error);
        return '';
      }
    }
  };

  /**
   * Fetches chat messages for the demo
   */
  const fetchChatMessages = async () => {
    if (!demoId) {
      return;
    } else {
      setChatLoading(true);
      try {
        const response = await getChatMessages(demoId);
        if (response?.data?.data) {
          setChatMessages(response.data.data);
        }
      } catch (error) {
        logger.error('Error fetching chat messages:', error);
        setChatMessages([]);
      } finally {
        setChatLoading(false);
      }
    }
  };

  /**
 * Fetches pros and cons for the demo
 */
const fetchProsCons = async () => {
  if (!demoId) {
    return;
  } else {
    setProsConsLoading(true);
    try {
      const response = await getDemoProsCons(demoId);
      if (response?.data) {
        setProsConsData({
          pros: response.data.pros || [],
          cons: response.data.cons || [],
          // The endpoint returns an empty, flagged payload when the store's
          // plan excludes insights, so an empty list can be told apart from a
          // locked one and prompted on accordingly.
          locked: Boolean(response.data.locked)
        });
      }
    } catch (error) {
      logger.error('Error fetching pros and cons:', error);
      setProsConsData({ pros: [], cons: [], locked: false });
    } finally {
      setProsConsLoading(false);
    }
  }
};

  /**
   * Renders a single chat message item
   *
   * @param {Object} chat - The chat message object
   * @param {number} index - The index of the chat message
   * @param {number} totalMessages - The total number of messages
   * @return {ReactNode} A React element that renders a single chat message
   */
  const renderChatMessage = (chat, index, totalMessages) => {
    let borderBottom = 'none';
    if (index < totalMessages - 1) {
      borderBottom = '1px solid #f0f0f0';
    }

    const userName = chat.user_name || 'Unknown User';
    const messageTime = chat.chat_message?.message_time || chat.message_time;
    const message = chat.chat_message?.message || chat.message || '';
    const extraGuestNames = guest?.extras?.map((g) => g?.guest_detail?.full_name) || [];
    const allGuestNames = [
      guest?.primary?.full_name,
      ...extraGuestNames
    ];

    const isGuest = allGuestNames.includes(userName);
    const isHost =
      userName ===
      `${host?.first_name} ${host?.last_name?.charAt(0)}.`;


    return (
      <div
        key={chat.id || index}
        className="chat-message-item"
        style={{
          marginBottom: '16px',
          padding: '12px',
          borderBottom
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div style={{
            fontWeight: isGuest ? 600 : 600,
            fontSize: isGuest ? "15px" : "15px",
            color: isGuest ? "#000000" : "#1890ff",
            marginBottom: "2px"
          }}>
            {userName}
          </div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            {formatMessageTime(messageTime)}
          </div>
        </div>
        <div className="text-wrap-break" style={{ color: '#333', lineHeight: '1.5' }}>
          {message}
        </div>
      </div>
    );
  };

  /**
   * Renders the chat messages list
   *
   * @return {ReactNode} A React element that renders the chat messages list
   */
  const renderChatMessagesList = () => {
    return (
      <div className="chat-messages-list">
        {chatMessages.map((chat, index) => renderChatMessage(chat, index, chatMessages.length))}
      </div>
    );
  };

  /**
   * Renders the chat container content based on loading and message states
   *
   * @return {ReactNode} A React element that renders the appropriate chat content
   */
  const renderChatContent = () => {
    if (chatLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Loading chats...
        </div>
      );
    }

    if (chatMessages.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
          Users did not use Chat feature
        </div>
      );
    }

    return renderChatMessagesList();
  };

  /**
 * Determines if a pro/con belongs to host or guest
 *
 * @param {string} userId - The user_id from pros/cons data
 * @return {string} Returns 'Host' or 'Guest'
 */
  const getUserType = (userId) => {
    if (!userId) return "Unknown";

    // Host by ID or email
    if (
      (host?.id && userId === host.id) ||
      (host?.email && userId === host.email)
    ) {
      return "Host";
    }

    // Primary guest by id or email
    if (
      (guest?.primary?.id && userId === guest.primary.id) ||
      (guest?.primary?.email && userId === guest.primary.email)
    ) {
      return "Guest";
    }

    // Extra guests by id or email
    const extraMatch = guest?.extras?.find(
      (eg) =>
        eg?.guest_detail?.id === userId ||
        eg?.guest_detail?.email === userId
    );

    if (extraMatch) {
      return "Guest";
    }

    return "Unknown";
  };


  /**
 * Renders a single pro or con item
 *
 * @param {Object} item - The pro or con object
 * @param {string} type - Either 'pro' or 'con'
 * @param {number} index - The index of the item
 * @return {ReactNode} A React element that renders a single pro/con item
 */
  const renderProsConsItem = (item, type, index) => {
    const detail = type === 'pro' ? item.pro_detail : item.con_detail;
    const originalText = type === 'pro' ? item.original_pro : item.original_con;
    const userType = getUserType(item.user_id);
    let userName = null;

    if (userType === "Guest") {
      if (guest?.primary?.id === item.user_id || guest?.primary?.email === item.user_id) {
        userName = guest?.primary?.full_name;
      } else {
        const extra = guest?.extras?.find(
          eg =>
            eg?.guest_detail?.id === item.user_id ||
            eg?.guest_detail?.email === item.user_id
        );
        userName = extra?.guest_detail?.full_name || null;
      }
    }

    return (
      <div
        key={item.id || index}
        className="pros-cons-item"
        style={{
          marginBottom: '12px',
          padding: '12px',
          backgroundColor: type === 'pro' ? '#f6ffed' : '#fff2f0',
          borderLeft: `4px solid ${type === 'pro' ? '#52c41a' : '#ff4d4f'}`,
          borderRadius: '4px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', flex: 1 }}>
            <div
              style={{
                fontWeight: '600',
                color: type === 'pro' ? '#52c41a' : '#ff4d4f',
                marginRight: '8px',
                fontSize: '16px'
              }}
            >
              {type === 'pro' ? '✓' : '✗'}
            </div>
            <div style={{ flex: 1 }}>
              {/* {detail && (
                <div style={{ fontWeight: '500', color: '#333', marginBottom: '4px' }}>
                  {detail.pro_text || detail.con_text || 'N/A'}
                </div>
              )} */}
              {originalText && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <div style={{ fontWeight: 500, color: "#333" }}>
                    "{originalText}"
                  </div>

                  {/* SHOW NAME ONLY FOR GUEST */}
                  {userName && (
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      — {userName}
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
          {/* <div
            style={{
              fontSize: '12px',
              fontWeight: '600',
              padding: '4px 12px',
              borderRadius: '12px',
              backgroundColor: userType === 'Host' ? '#e6f7ff' : '#f9f0ff',
              color: userType === 'Host' ? '#1890ff' : '#722ed1',
              marginLeft: '12px',
              whiteSpace: 'nowrap'
            }}
          >
            {userType}
          </div> */}
        </div>
      </div>
    );
  };

  /**
 * Separates pros/cons by user type (Host or Guest)
 *
 * @param {Array} items - Array of pros or cons
 * @return {Object} Object with hostItems and guestItems arrays
 */
  const separateByUserType = (items) => {
    const hostItems = [];
    const guestItems = [];
    const unknownItems = [];
    
    items.forEach(item => {
      const userType = getUserType(item.user_id);
      if (userType === 'Host') {
        hostItems.push(item);
      } else if (userType === 'Guest') {
        guestItems.push(item);
      } else {
        unknownItems.push(item);
      }
    });
    
    return { hostItems, guestItems, unknownItems };
  };

  /**
 * Renders pros or cons section with host/guest separation
 *
 * @param {Array} items - Array of pros or cons
 * @param {string} type - Either 'pro' or 'con'
 * @return {ReactNode} A React element that renders the section
 */
  const renderProsConsSection = (items, type) => {
    if (items.length === 0) return null;
    
    const { hostItems, guestItems, unknownItems } = separateByUserType(items);
    const sectionTitle = type === 'pro' ? 'Pros' : 'Cons';
    const sectionColor = type === 'pro' ? '#52c41a' : '#ff4d4f';
    
    return (
      <div className={`${type}s-section`} style={{ marginBottom: '24px' }}>
        <h4 style={{ color: sectionColor, marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>
          {sectionTitle} 
        </h4>
        
        {hostItems.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <h5 style={{ fontSize: '14px', color: '#050505', marginBottom: '8px', fontWeight: '600' }}>
              Real Customer {sectionTitle} 
            </h5>
            {hostItems.map((item, index) => renderProsConsItem(item, type, index))}
          </div>
        )}
        
        {guestItems.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <h5 style={{ fontSize: '14px', color: '#050505', marginBottom: '8px', fontWeight: '600' }}>
              Guest {sectionTitle} 
            </h5>
            {guestItems.map((item, index) => renderProsConsItem(item, type, index))}
          </div>
        )}
        
        {unknownItems.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <h5 style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '8px', fontWeight: '600' }}>
              Other {sectionTitle} 
            </h5>
            {unknownItems.map((item, index) => renderProsConsItem(item, type, index))}
          </div>
        )}
      </div>
    );
  };

  /**
 * Renders the pros and cons content based on loading and data states
 *
 * @return {ReactNode} A React element that renders the appropriate pros/cons content
 */
  const renderProsConsContent = () => {
    if (prosConsLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Loading pros and cons...
        </div>
      );
    }

    if (prosConsData.locked) {
      return (
        <UpgradePrompt
          variant="block"
          message="Pros and cons are not included in this store's plan."
        />
      );
    }

    const hasPros = prosConsData.pros.length > 0;
    const hasCons = prosConsData.cons.length > 0;

    if (!hasPros && !hasCons) {
      return (
        <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
          No pros and cons available for this demo
        </div>
      );
    }

    return (
      <div className="pros-cons-container">
        {hasPros && renderProsConsSection(prosConsData.pros, 'pro')}
        {hasCons && renderProsConsSection(prosConsData.cons, 'con')}
      </div>
    );
  };

  useEffectOnce(() => {
    profilePicture();
    videoUrl();
  }, []);

  useEffect(() => {
    if (demoId) {
      fetchChatMessages();
      fetchProsCons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoId]);

  const cellStyle = {
    display: "flex",
    alignItems: "flex-start",
    gap: "6px",
    textAlign: "left",
    wordBreak: "nowrap",
  };

  const couponStyle = {
    whiteSpace: "nowrap",
  };

  return (
    <div className="detail-payment-store-wrapper ">
      <div className="product-wrapper text-center">
        <div className="coupon-details">
          <p className="coupon-header">Coupon Code: {'  '}</p>
          <p className="coupon-details-text">
            {' '}
            {coupon_code || '-'}
            {coupon_code && <CopyIcon elementText={coupon_code} />}
          </p>
        </div>
        <div className="product-details">
          <p className="product-name-header">Product:</p>
          <p> {product.name}</p>
        </div>
        <div className="product-image-host-guest">
          <div className="image-product-square">
            <ImgComponent
              className="product-image-circle"
              src={product.image ? product.image : Icon}
              alt="product"
            />
          </div>
          <div className="host-details">
            <h5>Real Customer</h5>
            <div className="host-detail text-start">
              <div className="host-modal-image">
                <ImgComponent
                  className="host-custom-img"
                  src={
                    host?.profile_picture?.file !== null
                      ? hostProfile
                      : BlankImg
                  }
                  alt="user"
                />
              </div>
              <div className="host-image-details">
                <div style={{ display: "flex", alignItems: "center", color: "#1890ff" }}>
                  <span>
                    {host?.first_name} {host?.last_name.charAt(0)}.
                  </span>

                  {hasTechIssue("host", host?.id) && (
                    <OverlayTrigger
                      placement="top"
                      
                      overlay={<Tooltip>Real Customer faced technical difficulties</Tooltip>}
                    >
                      <img
                        src={TechDiffIcon}
                        alt="Tech Issue"
                        style={{ width: 14, height: 14, marginLeft: 6 }}
                      />
                    </OverlayTrigger>
                  )}
                </div>

                <div>{host?.email}</div>
                <div>{host?.user_profile?.phone_number}</div>
              </div>

            </div>
          </div>
          <div className="host-details">
            <h5>Guest</h5>

            {/* PRIMARY GUEST */}
            <div className="host-detail text-start">
              <div className="host-image-details">
                <p className="guest-detail-lineitem">
                  <span style={{ display: "flex", alignItems: "center" }}>
                    {guest?.primary?.full_name || '-'}
                    {guest?.primary?.full_name && (
                    <CopyIcon elementText={guest?.primary?.full_name} />
                  )}
                    {hasTechIssue("guest", guest?.primary?.id) && (
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Guest faced technical difficulties</Tooltip>}
                      >
                        <img
                          src={TechDiffIcon}
                          alt="Tech Issue"
                          style={{ width: 14, height: 14, marginLeft: 6 }}
                        />
                      </OverlayTrigger>
                    )}
                  </span>
                </p>
                <p className="guest-detail-lineitem">
                  <span>{guest?.primary?.email || '-'}</span>
                  {guest?.primary?.email && (
                    <CopyIcon elementText={guest?.primary?.email} />
                  )}
                </p>

                <p className="guest-detail-lineitem">
                  <span>{guest?.primary?.phone_number || '-'}</span>
                  {guest?.primary?.phone_number && (
                    <CopyIcon elementText={guest?.primary?.phone_number} />
                  )}
                </p>
              </div>
            </div>            
          </div>
        </div>

        {/* +X EXTRA GUESTS */}
        {guest?.extras?.length > 0 && (
          <p
            onClick={() => setShowExtraGuests(!showExtraGuests)}
            style={{
              color: '#0d6efd',
              cursor: 'pointer',
              marginTop: '8px',
              fontWeight: 500
            }}
          >
            {/* +{guest.extras.length} extra guest
            {guest.extras.length > 1 ? 's' : ''} */}
          </p>
        )}
              
        {guest?.extras?.length > 0 && (
          <div style={{ marginTop: "20px", width: "100%" }}>
            <Collapse
              bordered={false}
              size="small"
              items={[
                {
                  key: "1",
                  label: (
                    <span style={{ fontSize: "14px", fontWeight: 500 }}>
                      Extra Guest{guest.extras.length > 1 ? "s" : ""}
                    </span>
                  ),
                  children: (
                    <div
                      style={{
                        fontSize: "13px",
                        padding: "8px 4px",
                        textAlign: "left",
                        overflowX: "auto",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "150px 250px 120px 200px",
                          paddingBottom: "6px",
                          marginBottom: "6px",
                          borderBottom: "1px solid #e5e5e5",
                          fontWeight: 600,
                        }}
                      >
                        <div style={{ textAlign: "left" }}>Name</div>
                        <div style={{ textAlign: "left" }}>Email</div>
                        <div style={{ textAlign: "left" }}>Phone</div>
                        <div style={{ textAlign: "left" }}>Coupon Code</div>
                      </div>

                      {guest.extras.map((eg, i) => (
                        <div
                          key={i}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "150px 250px 120px 200px",
                            padding: "6px 0",
                            borderBottom:
                              i !== guest.extras.length - 1
                                ? "1px solid #efefef"
                                : "none",
                          }}
                        >
                          <div style={cellStyle}>
                            <span>{eg?.guest_detail?.full_name || "-"}</span>

                            {hasTechIssue("guest", eg?.guest_detail?.id) && (
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Extra guest faced technical difficulties</Tooltip>}
                              >
                                <img
                                  src={TechDiffIcon}
                                  alt="Tech Issue"
                                  style={{ width: 12, height: 12, marginLeft: 6 }}
                                />
                              </OverlayTrigger>
                            )}
                          </div>

                          <div style={cellStyle}>{eg?.guest_detail?.email || "-"}</div>
                          <div style={cellStyle}>{eg?.guest_detail?.phone_number || "-"}</div>
                          <div style={cellStyle}>
                            {eg?.guest_detail?.coupon_codes ? (
                              <div style={couponStyle}>
                                <span>{eg.guest_detail.coupon_codes}</span>
                              </div>
                            ) : (
                              "-"
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ),
                },
              ]}
            />
          </div>
        )}

      </div>{' '}
      <hr />
      <div className="video-earning-status">
        <div className="video-container">{getVideoUI()}</div>
        <div className="earning-status">
          <p>
            <span className="earnings-header">Status: </span>
            {earning?.earning_paid ? 'Paid' : 'Unpaid'}{' '}
          </p>
          <p>
            <span className="earnings-header">Earnings: </span>{' '}
            {earning?.earning ? `$${earning?.earning}` : '$0.00'}{' '}
          </p>
          <p>
            <span className="earnings-header">Meeting Rating: </span>{' '}
            {stars !== undefined ? (
              <Rating
                size={25}
                readonly
                transition
                allowFraction
                initialValue={stars}
              />
            ) : (
              '-'
            )}{' '}
          </p>
          <p className="text-wrap-break">
            <span className="earnings-header">Real Customer Review: </span>{' '}
            {review ? review : '-'}{' '}
          </p>
          {guestReviews.length > 0 && (
            <div style={{ marginTop: "12px" }}>
              <Collapse
                bordered={false}
                size="small"
                items={[
                  {
                    key: "guestReviewCollapse",
                    label: (
                      <span style={{ fontSize: "14px", fontWeight: 500,}}>
                        Guest Reviews
                      </span>
                    ),
                    children: (
                      <div
                        style={{
                          fontSize: "13px",
                          padding: "8px 4px",
                          textAlign: "left",
                          overflowX: "auto",
                          width: "100%",
                        }}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "3fr 3fr",
                            paddingBottom: "6px",
                            marginBottom: "6px",
                            borderBottom: "1px solid #e5e5e5",
                            fontWeight: 600,
                          }}
                        >
                          <div>Name</div>
                          <div>Review</div>
                        </div>
                        {guestReviews.map((gr, i) => (
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1.4fr 2.7fr",
                              alignItems: "flex-start",
                              paddingBottom: "6px",
                              marginBottom: "6px",
                              borderBottom: "1px solid #e5e5e5",
                              fontWeight: 500,
                            }}
                          >
                            <div style={{ fontWeight: 500 }}>
                              {gr.name}{" "}
                              <span style={{ fontSize: "11px", color: "#999" }}>
                              </span>
                            </div>

                            <div
                              className="text-wrap-break"
                              style={{
                                color: "#333",
                                wordBreak: "break-word",
                                whiteSpace: "normal",
                                lineHeight: "1.4"
                              }}
                            >
                              {gr.review}
                            </div>
                          </div>
                        ))}
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          )}

          <p>
            <span className="earnings-header">Sentiment Score: </span>{' '}
            {sentimentLocked ? (
              <UpgradePrompt message="Sentiment analysis is not included in this store's plan." />
            ) : (
              sentimentScore ? sentimentScore : '-'
            )}{' '}
          </p>

          <p className="text-wrap-break">
            <span className="earnings-header">Summary: </span>{' '}
            {summaryLocked ? (
              <UpgradePrompt message="AI meeting summaries are not included in this store's plan." />
            ) : (
              summary ? summary : hasDiscussedBusinessStatus === "NOT_APPLICABLE" ? '-' : "Processing"
            )}{' '}
          </p>
        </div>
      </div>
      <div className="demo-analysis-section" style={{ marginTop: '15px' }}>
        <Collapse
          size="large"
          items={[
            {
              key: '1',
              label: 'Transcript',
              children: (
                <Transcript
                  demo_analysis={demo_analysis}
                  meetingParticipants={meetingParticipants}
                  host={host}
                  guest={guest}
                  hasDiscussedBusinessStatus={hasDiscussedBusinessStatus}
                />
              )
            },
            {
              key: '2',
              label: 'Chats',
              children: (
                <div className="chats-container" style={{ padding: '10px 0' }}>
                  {renderChatContent()}
                </div>
              ),
            },
            {
              key: '3',
              label: 'Pros and cons',
              children: (
                <div className="pros-cons-wrapper" style={{ padding: '10px 0' }}>
                  {renderProsConsContent()}
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};

StorePayDetail.propTypes = {
  earning: PropTypes.object,
  guest: PropTypes.object,
  host: PropTypes.object,
  product: PropTypes.object,
  recording: PropTypes.object,
  startedAt: PropTypes.string,
  endedAt: PropTypes.string,
  soonestAt: PropTypes.string,
  stars: PropTypes.number,
  review: PropTypes.string,
  schedule: PropTypes.object,
  coupon_code: PropTypes.string,
  sentimentScore: PropTypes.string,
  summary: PropTypes.string,
  demo_analysis: PropTypes.array,
  meetingParticipants: PropTypes.array,
  hasDiscussedBusinessStatus: PropTypes.string,
  faceTimeDuration: PropTypes.string,
  demoId: PropTypes.string
};

export default StorePayDetail;
